import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { generatePath, Link as RouterLink, useLocation } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';

import { JOB_ORDER_TIME_STATUSES } from '../../constants/jobOrderConstants';
import { PRIMARY_COLOR } from '../../theme/colorConstants';
import { ROUTE_CONSTANTS } from '../../views/app/RouteBuilding/routeConstants';
import FlipCard from '../FlipCard';
import ActiveRosterView from '../ShiftCard/ActiveRosterView';
import CompletedRosterView from '../ShiftCard/CompletedRosterView';
import UpcomingRosterView from '../ShiftCard/UpcomingRosterView';

import JobOrderCardChrome from './JobOrderCardChrome';
import JobOrderCardFront from './JobOrderCardFront';
import { getPrimaryActionKey } from './jobOrderCardModel';

const JobOrderCard = ({
  jobOrder,
  index,
  date,
  moveCard,
  isFlipped,
  handleFlipClick,
  onPlacementCheckStatusUpdate,
  handleTimecardEditSubmit,
  onStatusUpdate,
  clearTimeCardError,
  timecardIsUpdating,
  timecardUpdateError,
  isCancelled,
  timeStatus,
  showSelection,
  checked,
  disabled,
  onToggle,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const primaryActionLabel = LanguageConverterV2(getPrimaryActionKey(timeStatus));
  const collapseCardLabel = LanguageConverterV2('roster.collapseCard');
  const detailPath = jobOrder?._id
    ? `${generatePath(`/${ROUTE_CONSTANTS.JOB_ORDERS}/:id`, { id: jobOrder._id })}${
        location.search
      }`
    : '';

  return (
    <div data-testid="job-order-card-redesign">
      <FlipCard
        frontContent={
          <JobOrderCardFront
            jobOrder={jobOrder}
            timeStatus={timeStatus}
            isCancelled={isCancelled}
          />
        }
        backContent={
          (timeStatus === JOB_ORDER_TIME_STATUSES.ACTIVE && (
            <ActiveRosterView
              isExpanded={isExpanded}
              jobOrder={jobOrder}
              onPlacementCheckStatusUpdate={onPlacementCheckStatusUpdate}
              clearTimeCardError={clearTimeCardError}
              handleTimecardEditSubmit={handleTimecardEditSubmit}
              onStatusUpdate={onStatusUpdate}
              timecardIsUpdating={timecardIsUpdating}
              timecardUpdateError={timecardUpdateError}
              timeStatus={timeStatus}
              redesign
            />
          )) ||
          (timeStatus === JOB_ORDER_TIME_STATUSES.UPCOMING && (
            <UpcomingRosterView
              isExpanded={isExpanded}
              jobOrder={jobOrder}
              hideDetailButton
              redesign
            />
          )) ||
          (timeStatus === JOB_ORDER_TIME_STATUSES.COMPLETED && (
            <CompletedRosterView
              isExpanded={isExpanded}
              jobOrder={jobOrder}
              clearTimeCardError={clearTimeCardError}
              handleTimecardEditSubmit={handleTimecardEditSubmit}
              onStatusUpdate={onStatusUpdate}
              timecardIsUpdating={timecardIsUpdating}
              timecardUpdateError={timecardUpdateError}
              timeStatus={timeStatus}
              redesign
            />
          ))
        }
        index={index}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        date={date}
        moveCard={moveCard}
        isFlipped={isFlipped}
        handleFlipClick={handleFlipClick}
        isCancelled={isCancelled}
        timeStatus={timeStatus}
        showSelection={showSelection}
        checked={checked}
        disabled={disabled}
        onToggle={onToggle}
        cardWidth={320}
        cardHeight={540}
        expandedHeight={963}
        renderHeader={({ isFlipped: headerFlipped }) => (
          <JobOrderCardChrome
            jobOrder={jobOrder}
            timeStatus={timeStatus}
            isCancelled={isCancelled}
            isFlipped={headerFlipped}
            onFlipClick={handleFlipClick}
            showSelection={showSelection}
            checked={checked}
            disabled={disabled}
            onToggle={onToggle}
          />
        )}
        selectedBorder
        borderRadius="16px"
        collapseLabel={collapseCardLabel}
        redesign
        footerAction={
          detailPath ? (
            <RouterLink
              data-testid="job-order-card-primary-action"
              to={detailPath}
              style={{
                display: 'block',
                border: `1px solid ${PRIMARY_COLOR[70]}`,
                color: PRIMARY_COLOR[70],
                borderRadius: 8,
                padding: '8px 12px',
                textAlign: 'center',
                textDecoration: 'none',
                fontFamily: 'inherit',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: '#fff',
              }}
            >
              {primaryActionLabel}
            </RouterLink>
          ) : null
        }
      />
    </div>
  );
};

JobOrderCard.propTypes = {
  jobOrder: PropTypes.shape({
    _id: PropTypes.string,
    placements: PropTypes.arrayOf(PropTypes.shape({})),
    name: PropTypes.string,
  }),
  index: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  moveCard: PropTypes.func.isRequired,
  isFlipped: PropTypes.bool.isRequired,
  handleFlipClick: PropTypes.func.isRequired,
  onPlacementCheckStatusUpdate: PropTypes.func.isRequired,
  clearTimeCardError: PropTypes.func.isRequired,
  handleTimecardEditSubmit: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func.isRequired,
  timecardUpdateError: PropTypes.string,
  timecardIsUpdating: PropTypes.bool,
  isCancelled: PropTypes.bool.isRequired,
  timeStatus: PropTypes.string.isRequired,
  showSelection: PropTypes.bool,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default JobOrderCard;
